/**
 * EditToolbar - Toolbar displayed when dashboard is in edit mode
 *
 * Features:
 * - Edit mode badge
 * - Unsaved changes indicator
 * - Save/Cancel/Reset/Exit buttons
 * - Error display
 * - Loading states
 */

import React from 'react';
import { useEditMode } from '../../contexts/EditModeContext';
import './EditToolbar.css';

const EditToolbar = () => {
  const {
    currentDashboardId,
    hasUnsavedChanges,
    saveInProgress,
    error,
    saveChanges,
    cancelChanges,
    exitEditMode,
    resetToDefault
  } = useEditMode();

  const handleSave = async () => {
    try {
      await saveChanges();
    } catch (err) {
      // Error is set in context
      console.error('Save failed:', err);
    }
  };

  const handleCancel = () => {
    const cancelled = cancelChanges();
    if (cancelled) {
      exitEditMode(false);
    }
  };

  const handleReset = async () => {
    try {
      await resetToDefault('all');
    } catch (err) {
      // Error is set in context
      console.error('Reset failed:', err);
    }
  };

  const handleExit = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Exit without saving?'
      );
      if (!confirmed) {
        return;
      }
    }
    exitEditMode(false);
  };

  return (
    <div className="edit-toolbar">
      <div className="edit-toolbar-content">
        {/* Left Section - Status */}
        <div className="edit-toolbar-left">
          <span className="edit-mode-badge">✏️ Edit Mode</span>
          <span className="dashboard-name">{currentDashboardId}</span>
          {hasUnsavedChanges && (
            <span className="unsaved-indicator" title="You have unsaved changes">
              ● Unsaved Changes
            </span>
          )}
        </div>

        {/* Right Section - Actions */}
        <div className="edit-toolbar-right">
          <button
            className="toolbar-btn btn-save"
            onClick={handleSave}
            disabled={!hasUnsavedChanges || saveInProgress}
            title="Save changes to Snowflake"
          >
            {saveInProgress ? '⏳ Saving...' : '💾 Save Changes'}
          </button>

          <button
            className="toolbar-btn btn-cancel"
            onClick={handleCancel}
            disabled={saveInProgress}
            title="Discard changes and exit edit mode"
          >
            Cancel
          </button>

          <button
            className="toolbar-btn btn-reset"
            onClick={handleReset}
            disabled={saveInProgress}
            title="Reset dashboard to default configuration"
          >
            🔄 Reset to Default
          </button>

          <button
            className="toolbar-btn btn-exit"
            onClick={handleExit}
            disabled={saveInProgress}
            title="Exit edit mode"
          >
            ✖ Exit
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="edit-toolbar-error">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error}</span>
        </div>
      )}
    </div>
  );
};

export default EditToolbar;
